import { hasOverridableRestriction, requiredIntegerMoreThanZero } from "common";
import { isNil } from "lodash";
import {
    AmountType,
    CatalogItemDisplaySection,
    CatalogItemRestriction,
    CatalogItemRestrictionScope,
    CatalogType,
    DisplayStepOption,
    Experience,
    Field,
    FieldType,
    FieldVisibility,
    Form,
    LineItemCode,
    ProductCatalogItem,
    QuantitySource,
    Seller,
} from "types";
import { ValidationError } from "yup";
import { AddOnEditorFormState } from "./AddOnTypes";
import { queryClient } from "@/api/queryClient";
import { createNewForm } from "@/models/Form/Form";
import { getSeller } from "@/utils/getSeller";

export const hasQuestionnaireDisplaySection = (catalogItem: {
    displaySections?: CatalogItemDisplaySection[] | null;
}): boolean => {
    return !!catalogItem.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
};

export const formIncludesQuestionnaire = (formValues: {
    displaySections?: CatalogItemDisplaySection[] | null;
}): boolean => {
    return !!formValues.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
};

/** Only a checkbox add-on can match the guest count. Every other add-on keeps `quantitySource` null. */
export const canMatchGuestCount = (
    formValues: Pick<AddOnEditorFormState, "type" | "priceType" | "displaySections">,
) => {
    return (
        formValues.type === CatalogType.BOOLEAN &&
        formValues.priceType !== AmountType.PERCENT &&
        !formIncludesQuestionnaire(formValues)
    );
};

/** Switching type clears the limits and the guest-count match, since the new type may not support them. */
export const getTypeSwitchChanges = (type: CatalogType): Partial<AddOnEditorFormState> => {
    return {
        restrictions: [],
        quantitySource: null,
        ...(type === CatalogType.QUANTITY && { priceType: AmountType.ABSOLUTE }),
    };
};

export const hasQuestionnaireSelection = (
    catalogItem: { displaySections?: CatalogItemDisplaySection[] | null },
    formValues: { displaySections?: CatalogItemDisplaySection[] | null },
): boolean => {
    return hasQuestionnaireDisplaySection(catalogItem) || formIncludesQuestionnaire(formValues);
};
export interface FormUpdateParams {
    catalogItem: ProductCatalogItem;
    formValues: AddOnEditorFormState;
    globalQuestionnaire?: Form;
    questionnaireForm?: Form;
    experience: Experience;
    saveForm: (form: Form) => Promise<any>;
    saveQuestionnaireForSavedViews?: (form: Form, productId: string) => Promise<void>;
}

/**
 * Creates a product-level form by copying fields from global questionnaire
 */
const createProductFormFromGlobal = (seller: Seller, experience: Experience, globalForm?: Form): Form => {
    const newForm = createNewForm(seller, false, experience);

    if (globalForm?.fields) {
        // Copy all fields from global form to product form
        newForm.fields = globalForm.fields.map((field) => {
            return { ...field, id: undefined };
        }) as unknown as Field[];
    }

    return newForm;
};

/**
 * Creates or updates a form field for an add-on
 */
const createOrUpdateFormField = (
    currentFields: Field[],
    catalogItem: ProductCatalogItem,
    formValues: AddOnEditorFormState,
): { needsUpdate: boolean; updatedFields: Field[] } => {
    const correspondingField = currentFields.find((field: Field) => field.catalogItem?.id === catalogItem.id);

    if (!correspondingField) {
        // Create new form field
        const newField = {
            label: catalogItem.name,
            organizerOnly: false,
            required: formValues.required ?? false,
            roster: formValues.roster ?? false,
            addToSavedViews: formValues.addToSavedViews ?? false,
            type: FieldType.ADDON,
            visibility: FieldVisibility.ALL,
            catalogItem: catalogItem,
            displayStep: DisplayStepOption.BEFORE_PAYMENT,
            readonly: false,
            seq: (currentFields.length + 1) * 10,
        };

        return {
            needsUpdate: true,
            updatedFields: [...currentFields, newField as unknown as Field],
        };
    }

    const hasAddOnSettingsChanged = correspondingField.required !== (formValues.required ?? false);

    const hasRosterSettingsChanged =
        correspondingField.roster !== (formValues.roster ?? false) ||
        correspondingField.addToSavedViews !== (formValues.addToSavedViews ?? false);

    if (!hasRosterSettingsChanged && !hasAddOnSettingsChanged) {
        return { needsUpdate: false, updatedFields: currentFields };
    }

    const updatedFields = currentFields.map((field) => {
        if (field.id === correspondingField.id) {
            return {
                ...field,
                roster: formValues.roster ?? false,
                addToSavedViews: formValues.addToSavedViews ?? false,
                required: formValues.required ?? false,
            };
        }
        return field;
    });

    return { needsUpdate: true, updatedFields };
};

/**
 * Removes a form field for an add-on
 */
const removeFormField = (
    currentFields: Field[],
    catalogItemId: string,
): { needsUpdate: boolean; updatedFields: Field[] } => {
    const correspondingField = currentFields.find((field) => field.catalogItem?.id === catalogItemId);

    if (!correspondingField) {
        return { needsUpdate: false, updatedFields: currentFields };
    }

    const updatedFields = currentFields.filter((field) => field.id !== correspondingField.id);
    return { needsUpdate: true, updatedFields };
};

/**
 * Updates a single form with add-on field changes
 */
const updateSingleForm = async (
    form: Form,
    catalogItem: ProductCatalogItem,
    formValues: AddOnEditorFormState,
    isGuestQuestionnaire: boolean,
    saveForm: (form: Form) => Promise<any>,
): Promise<{ wasUpdated: boolean; updatedForm: Form }> => {
    let needsUpdate = false;
    let updatedFields = form.fields;

    if (isGuestQuestionnaire) {
        const result = createOrUpdateFormField(form.fields, catalogItem, formValues);
        needsUpdate = result.needsUpdate;
        updatedFields = result.updatedFields;
    } else {
        if (catalogItem.id) {
            const result = removeFormField(form.fields, catalogItem.id);
            needsUpdate = result.needsUpdate;
            updatedFields = result.updatedFields;
        }
    }

    const updatedForm = { ...form, fields: updatedFields };

    if (needsUpdate) {
        await saveForm(updatedForm);
    }

    return { wasUpdated: needsUpdate, updatedForm };
};

export const updateAddOnFormFields = async ({
    catalogItem,
    formValues,
    globalQuestionnaire,
    questionnaireForm,
    experience,
    saveForm,
    saveQuestionnaireForSavedViews,
}: FormUpdateParams): Promise<void> => {
    const seller = getSeller();
    const isGuestQuestionnaire = hasQuestionnaireSelection(catalogItem, formValues);
    let formToUpdate = questionnaireForm;

    // Handle questionnaire creation scenarios when needed
    if (!formToUpdate && isGuestQuestionnaire) {
        formToUpdate = !globalQuestionnaire
            ? createNewForm(seller, false, experience)
            : createProductFormFromGlobal(seller, experience, globalQuestionnaire);
    }

    if (!formToUpdate) {
        return;
    }

    const { wasUpdated, updatedForm } = await updateSingleForm(
        formToUpdate,
        catalogItem,
        formValues,
        isGuestQuestionnaire,
        saveForm,
    );

    if (wasUpdated) {
        queryClient.invalidateQueries("forms");

        if (saveQuestionnaireForSavedViews && isGuestQuestionnaire) {
            try {
                await saveQuestionnaireForSavedViews(updatedForm, experience.id);
            } catch (error) {
                console.error("Failed to update saved views after form change:", error);
            }
        }
    }
};

export const LABEL_BY_QUANTITY_SOURCE: Record<QuantitySource, string> = {
    [LineItemCode.DEMOGRAPHIC]: "demographic",
    [LineItemCode.PRIMARY_UNIT]: "purchasable item",
};

const RESTRICTION_SCOPES_BY_TYPE: Partial<Record<CatalogType, CatalogItemRestrictionScope[]>> = {
    [CatalogType.QUANTITY]: [
        CatalogItemRestrictionScope.EVENT,
        CatalogItemRestrictionScope.PURCHASE_ITEM,
        CatalogItemRestrictionScope.PRIMARY_UNIT,
    ],
    [CatalogType.BOOLEAN]: [CatalogItemRestrictionScope.EVENT],
};

export const getRestrictionScopesForType = (type: CatalogType) => RESTRICTION_SCOPES_BY_TYPE[type] ?? [];

/** A limit left over from another type is dropped, since the API rejects it. */
export const getRestrictionsForType = (type: CatalogType, restrictions: CatalogItemRestriction[] = []) => {
    const scopes = getRestrictionScopesForType(type);
    return restrictions.filter((restriction) => scopes.includes(restriction.scope));
};

export const getRestrictionValue = (restrictions: CatalogItemRestriction[], scope: CatalogItemRestrictionScope) => {
    return restrictions.find((restriction) => restriction.scope === scope)?.value;
};

/** A value of 0 means the limit is switched on but not set yet. Only the row's toggle removes a limit. */
export const setRestrictionValue = (
    restrictions: CatalogItemRestriction[],
    scope: CatalogItemRestrictionScope,
    value: number | undefined,
): CatalogItemRestriction[] => {
    const others = restrictions.filter((restriction) => restriction.scope !== scope);
    if (isNil(value)) {
        return others;
    }

    // A negative or invalid value becomes 0, so the row shows an error instead of switching the limit off mid-edit.
    const safeValue = Number.isNaN(value) || value < 0 ? 0 : value;

    const existing = restrictions.find((restriction) => restriction.scope === scope);
    // A new limit takes the override setting of the existing limits.
    const allowOverride = existing?.allowOverride ?? hasOverridableRestriction(others);

    return [...others, { ...existing, scope, value: safeValue, allowOverride }];
};

// Repeats moreThan(0) to give the error a message, since the helper's own has none.
export const limitValueSchema = requiredIntegerMoreThanZero().moreThan(0, "Value must be greater than 0");

const exceeds = (value: number | undefined, limit: number | undefined) => !!value && !!limit && value > limit;

export const getRestrictionError = (restrictions: CatalogItemRestriction[], scope: CatalogItemRestrictionScope) => {
    const value = getRestrictionValue(restrictions, scope);

    try {
        if (!isNil(value)) {
            limitValueSchema.validateSync(value);
        }
    } catch (error) {
        return (error as ValidationError).message;
    }

    return getRestrictionHierarchyError(restrictions, scope);
};

/**
 * Limits nest: per guest is at most per reservation, which is at most per event. Equal is allowed, since a private
 * event holds one reservation. The error shows on the narrower limit, the one to lower.
 */
export const getRestrictionHierarchyError = (
    restrictions: CatalogItemRestriction[],
    scope: CatalogItemRestrictionScope,
) => {
    const eventValue = getRestrictionValue(restrictions, CatalogItemRestrictionScope.EVENT);
    const reservationValue = getRestrictionValue(restrictions, CatalogItemRestrictionScope.PURCHASE_ITEM);

    if (scope === CatalogItemRestrictionScope.PURCHASE_ITEM && exceeds(reservationValue, eventValue)) {
        return "A per reservation limit cannot exceed the per event limit";
    }

    if (scope === CatalogItemRestrictionScope.PRIMARY_UNIT) {
        const guestValue = getRestrictionValue(restrictions, CatalogItemRestrictionScope.PRIMARY_UNIT);
        if (exceeds(guestValue, reservationValue)) {
            return "A per guest limit cannot exceed the per reservation limit";
        }
        if (exceeds(guestValue, eventValue)) {
            return "A per guest limit cannot exceed the per event limit";
        }
    }

    return undefined;
};
